/**
 * Agreement detail — `/hotel-supplier/agreements/:id`.
 *
 * The read half reuses `AgreementDetail`, the same component the expandable
 * table row renders, so the two surfaces cannot drift. What this page adds is
 * the WRITE half: advancing the lifecycle, and creating a successor (renew or
 * reopen).
 *
 * ── The lifecycle buttons come from the SERVER ──────────────────────────────
 * `allowed_transitions` and `successor_kind` are returned by the API, not
 * computed here. A client-side copy of the transition graph would drift and
 * start offering moves the API then rejects with a 409 — the same class of bug
 * the sortable-column drift guard exists to prevent. The consequence is worth
 * stating: this page can render a button for a status it has never heard of,
 * which is exactly what should happen when the vocabulary changes server-side.
 *
 * ── Modals, and only here ───────────────────────────────────────────────────
 * Every action is confirmed in a dialog, because each one is irreversible in a
 * way the user cares about: a status change is audited under their name, and
 * renew/reopen creates a real second agreement. This is the narrow case modals
 * were reserved for — the read surfaces stayed panels and routes precisely so
 * that a dialog here means "stop and look".
 *
 * ── Renew is a two-step on purpose ──────────────────────────────────────────
 * `preview: true` first, so the user sees exactly what will be copied and any
 * stale-date warnings BEFORE anything is written. Committing past outstanding
 * warnings requires acknowledging them. That is the literal implementation of
 * "if there are dates that are no longer valid, validate those and call them out
 * prior to saving the contract".
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { Modal } from '@/features/crm/components/Modal';
import { AgreementDetail, fmtDate, type Agreement } from './AgreementDetail';
import { StatusControl } from '../../components/StatusControl';

interface DetailResponse {
  agreement: Agreement;
  allowed_transitions: string[];
  successor_kind: 'renew' | 'reopen' | null;
}

interface DateWarning {
  field: string;
  value: string;
  message: string;
}

interface PreviewResponse {
  kind: 'renew' | 'reopen';
  proposed: Record<string, unknown>;
  warnings: DateWarning[];
  context: {
    previous_internal_note: string | null;
    previous_hotel_note: string | null;
    previous_currency: string | null;
  };
}

/** Human labels for the transition buttons. Falls back to the raw status. */
const TRANSITION_LABELS: Record<string, string> = {
  rfp_sent: 'Mark agreement sent',
  awaiting_response: 'Awaiting response',
  response_received: 'Response received',
  negotiating: 'Negotiating with property',
  accepted: 'Accept',
  loading: 'Send to loading',
  /*
   * ITEM 60 — "Loading → Active is not discoverable."
   *
   * It read "Mark live", and the person who commissioned the feature could not
   * find it. The problem was not that the words are wrong — a live rate IS
   * live — but that they name a state nobody looks for. The team navigates by
   * the TAB names: Sourcing, Loading, Renewal, Active. So the button now says
   * where the agreement goes, in the word already on screen.
   *
   * Note the rate audit advances this automatically on a passing test (§4 of
   * the 2026-08-31 plan). This button is the manual path for an agreement
   * verified outside the tool — which is exactly the case Stacie described for
   * the existing book: "move existing Loading statuses to Tested Successfully
   * so they move to the active section."
   */
  active: 'Move to Active',
  expired: 'Close — term expired',
  terminated: 'Terminate',
  declined: 'Declined by hotel',
  dismissed: 'Dismiss',
};

/** Transitions that end the agreement — styled and confirmed as such. */
const CLOSING = new Set(['expired', 'terminated', 'declined', 'dismissed']);

export default function AgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pending status change awaiting confirmation.
  const [pending, setPending] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Successor flow.
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [termStart, setTermStart] = useState('');
  const [termEnd, setTermEnd] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setData(await apiFetch<DetailResponse>(`/api/btp/agreements/${id}`));
    } catch (e) {
      setError(msgOf(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function commitStatus() {
    if (!pending || !id) return;
    setBusy(true);
    setActionError(null);
    try {
      const next = await apiFetch<DetailResponse>(`/api/btp/agreements/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: pending,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        }),
      });
      // The response carries the NEW allowed_transitions, so the buttons
      // re-render correctly without a second round trip.
      setData(next);
      setPending(null);
      setReason('');
    } catch (e) {
      setActionError(msgOf(e));
    } finally {
      setBusy(false);
    }
  }

  async function openSuccessorPreview() {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    try {
      setPreview(
        await apiFetch<PreviewResponse>(`/api/btp/agreements/${id}/renew`, {
          method: 'POST',
          body: JSON.stringify({ preview: true }),
        }),
      );
    } catch (e) {
      setActionError(msgOf(e));
    } finally {
      setBusy(false);
    }
  }

  async function commitSuccessor() {
    if (!id || !preview) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await apiFetch<{ agreement: { id: string } }>(
        `/api/btp/agreements/${id}/renew`,
        {
          method: 'POST',
          body: JSON.stringify({
            ...(termStart ? { term_start: termStart } : {}),
            ...(termEnd ? { term_end: termEnd } : {}),
            // Only sent when there is something to acknowledge — the server
            // 409s a commit that skips outstanding warnings.
            ...(preview.warnings.length > 0
              ? { acknowledge_warnings: true }
              : {}),
          }),
        },
      );
      // Land on the successor: it is in Sourcing and is the thing to work next.
      navigate(`/hotel-supplier/agreements/${res.agreement.id}`);
    } catch (e) {
      setActionError(msgOf(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="page" data-testid="supplier-agreement-detail-page">
        <div className="empty-state">
          <div className="empty-sub">Loading…</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="page" data-testid="supplier-agreement-detail-page">
        <div
          className="banner banner-error"
          data-testid="supplier-agreement-detail-error"
          role="alert"
        >
          {error ?? 'Agreement not found.'}
        </div>
        <Link className="btn btn-outline" to="/hotel-supplier/agreements">
          Back to Agreements
        </Link>
      </div>
    );
  }

  const a = data.agreement;
  const hotel = a.property?.propertyName ?? a.hotelMaster?.name ?? 'Agreement';

  return (
    <div className="page" data-testid="supplier-agreement-detail-page">
      <div className="page-header">
        <Link
          className="row-link"
          style={{ fontSize: 12.5 }}
          to="/hotel-supplier/agreements"
        >
          ← Agreements
        </Link>
        <h1 style={{ marginTop: 6 }}>{hotel}</h1>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: 6,
          }}
        >
          {/*
            THE SAME CHIP CONTROL AS THE LIST ROW. This page kept its own
            read-only pill plus a row of per-edge buttons; the buttons are gone
            and the pill is now the control, so there is ONE way to change a
            status in this module and it is the thing that displays it.
          */}
          <StatusControl
            agreementId={a.id}
            allowedTransitions={data.allowed_transitions}
            onChanged={() => void load()}
            status={a.status}
            testId="supplier-agreement-detail-status-control"
          />
          <Link
            className="row-link"
            style={{ fontFamily: 'SF Mono, Menlo, monospace', fontSize: 12 }}
            to={`/crm/customers/${encodeURIComponent(a.customerId)}`}
          >
            {a.customerId}
          </Link>
          {a.termEnd && (
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              Term ends {fmtDate(a.termEnd) ?? '—'}
            </span>
          )}
          {/* The renewal chain, both directions, so the history is walkable
              rather than something you have to know to look for. */}
          {a.supersedesId && (
            <Link
              className="row-link"
              data-testid="supplier-agreement-detail-supersedes"
              style={{ fontSize: 12.5 }}
              to={`/hotel-supplier/agreements/${a.supersedesId}`}
            >
              ← renews an earlier agreement
            </Link>
          )}
        </div>
      </div>

      {/* ── Actions ─────────────────────────────────────────────────────── */}
      <div className="section">
        <div className="section-header">
          <span className="section-label">Actions</span>
        </div>
        <div className="section-body">
          {data.allowed_transitions.length === 0 && !data.successor_kind && (
            <p
              data-testid="supplier-agreement-detail-no-actions"
              style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}
            >
              This agreement is closed and has already been superseded — there
              is nothing further to do with it.
            </p>
          )}

          {/*
           * THE PER-EDGE BUTTONS ARE GONE, replaced by the shared
           * `StatusControl` that `AgreementDetail` renders below — which also
           * puts it in the expanded row of the list, where Jeff went looking
           * for it and found nothing.
           *
           * One button per legal move was workable on a near-linear graph. It
           * is not workable now that sourcing is all-to-all: from any sourcing
           * status there are five siblings plus two closures, and seven primary
           * buttons in a row is a wall, not a control. Same reasoning that made
           * the list's stage pills a pill-bar and not seven links.
           *
           * Renew / reopen stay HERE and stay buttons: they create a SECOND
           * agreement rather than moving this one, so putting them in a "move
           * to…" dropdown would misrepresent what they do.
           */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {data.successor_kind && (
              <button
                className="btn btn-primary"
                data-testid="supplier-agreement-successor"
                disabled={busy}
                onClick={openSuccessorPreview}
                type="button"
              >
                {data.successor_kind === 'renew'
                  ? 'Renew — start a new agreement'
                  : 'Reopen — send back to sourcing'}
              </button>
            )}
          </div>

          {actionError && (
            <div
              className="banner banner-error"
              data-testid="supplier-agreement-action-error"
              role="alert"
              style={{ marginTop: 12 }}
            >
              {actionError}
            </div>
          )}
        </div>
      </div>

      <AgreementDetail
        agreement={a}
        // A passing rate audit advances the agreement, so the page has to look
        // again — the status pill and the action buttons both change.
        onAgreementMayHaveChanged={() => void load()}
      />

      {/* ── Confirm a status change ─────────────────────────────────────── */}
      {pending && (
        <ConfirmDialog
          busy={busy}
          confirmLabel={
            CLOSING.has(pending) ? 'Close agreement' : 'Change status'
          }
          onCancel={() => {
            setPending(null);
            setReason('');
          }}
          onConfirm={commitStatus}
          testId="supplier-agreement-confirm-status"
          title={TRANSITION_LABELS[pending] ?? `Move to ${pending}`}
        >
          <p style={{ fontSize: 13, marginTop: 0 }}>
            {a.status.replace(/_/g, ' ')} →{' '}
            <strong>{pending.replace(/_/g, ' ')}</strong>
            {CLOSING.has(pending) && (
              <>
                {' '}
                — this closes the agreement. It cannot be moved back afterwards;
                reopening creates a new agreement so the record of why it closed
                survives.
              </>
            )}
          </p>
          <label className="field-label" htmlFor="ad-reason">
            Reason {CLOSING.has(pending) ? '(recommended)' : '(optional)'}
          </label>
          <textarea
            className="textarea"
            data-testid="supplier-agreement-reason"
            id="ad-reason"
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            value={reason}
          />
          <p style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            Recorded in the audit log against your name.
          </p>
        </ConfirmDialog>
      )}

      {/* ── Confirm renew / reopen, with the warnings up front ──────────── */}
      {preview && (
        <ConfirmDialog
          busy={busy}
          confirmLabel={
            preview.kind === 'renew' ? 'Create renewal' : 'Reopen to sourcing'
          }
          onCancel={() => {
            setPreview(null);
            setTermStart('');
            setTermEnd('');
          }}
          onConfirm={commitSuccessor}
          testId="supplier-agreement-confirm-successor"
          title={
            preview.kind === 'renew'
              ? 'Renew this agreement'
              : 'Reopen to sourcing'
          }
        >
          {/* Warnings FIRST — the whole reason this is a two-step. */}
          {preview.warnings.length > 0 && (
            <div
              className="banner banner-warn"
              data-testid="supplier-agreement-successor-warnings"
              style={{ marginBottom: 12 }}
            >
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {preview.warnings.map((w) => (
                  <li key={w.field} style={{ fontSize: 12.5 }}>
                    {w.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p style={{ fontSize: 13, marginTop: 0 }}>
            A <strong>new</strong> agreement will be created in Sourcing,
            superseding this one. This agreement is not changed.
          </p>
          <p style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            Contacts, TMC, airports, amenities and the volume ask carry over.{' '}
            <strong>Pricing does not</strong> — rates, commission and discounts
            start blank, because they are what you are renegotiating.
          </p>

          <div className="form-grid" style={{ marginTop: 10 }}>
            <div>
              <label className="field-label" htmlFor="ad-ts">
                New term starts
              </label>
              <input
                className="input"
                id="ad-ts"
                onChange={(e) => setTermStart(e.target.value)}
                type="date"
                value={termStart}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="ad-te">
                New term ends
              </label>
              <input
                className="input"
                data-testid="supplier-agreement-successor-term-end"
                id="ad-te"
                onChange={(e) => setTermEnd(e.target.value)}
                type="date"
                value={termEnd}
              />
            </div>
          </div>

          {(preview.context.previous_internal_note ||
            preview.context.previous_hotel_note) && (
            <div style={{ marginTop: 12 }}>
              <span className="field-label">From the previous cycle</span>
              {preview.context.previous_internal_note && (
                <p style={{ fontSize: 12.5, margin: '2px 0' }}>
                  <em>Internal:</em> {preview.context.previous_internal_note}
                </p>
              )}
              {preview.context.previous_hotel_note && (
                <p style={{ fontSize: 12.5, margin: '2px 0' }}>
                  <em>Hotel:</em> {preview.context.previous_hotel_note}
                </p>
              )}
              <p style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                Shown for context — not copied into the new agreement.
              </p>
            </div>
          )}
        </ConfirmDialog>
      )}
    </div>
  );
}

/**
 * A confirm dialog, now a thin wrapper over the shared `Modal`.
 *
 * This function used to BE the modal, and said why: "this is the FIRST modal in
 * the CRM surface, and building a general-purpose dialog system for two call
 * sites would be inventing an abstraction before its second real use." The
 * second use is log-a-contact (Stacie, 2026-09-03), so the general version was
 * extracted to features/crm/components/Modal and this collapsed onto it.
 *
 * It gains what the local copy explicitly deferred — a focus trap, focus
 * restore on close, and a backdrop click that ignores drags out of the panel —
 * and keeps its own shape: two buttons, a busy state, and no dismissal at all
 * while a write is in flight.
 */
function ConfirmDialog({
  title,
  children,
  confirmLabel,
  onConfirm,
  onCancel,
  busy,
  testId,
}: {
  title: string;
  children: React.ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
  testId: string;
}) {
  return (
    <Modal
      actions={
        <>
          <button
            className="btn btn-outline"
            disabled={busy}
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            data-testid={`${testId}-confirm`}
            disabled={busy}
            onClick={onConfirm}
            type="button"
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
      // A write in flight is not dismissable: the action is already happening,
      // and closing over it would leave the user unsure whether it landed. This
      // is what the old `!busy` check on the Escape handler did.
      confirmDismiss={() => !busy}
      onClose={onCancel}
      testId={testId}
      title={title}
    >
      {children}
    </Modal>
  );
}

/** The API's own message where there is one — it is the precise one. */
function msgOf(e: unknown): string {
  if (e instanceof ApiError) {
    const b = e.body;
    if (typeof b === 'object' && b !== null && 'error' in b) {
      return String((b as { error: unknown }).error);
    }
    return e.message;
  }
  return String(e);
}
